import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { getCaptionsForSegment } from '@goldcast/player-config/src/utils/playerConfigUtils';
import { getAllCompositeSegments, getClipFPS } from '@goldcast/player-config/src/utils/clipUtils';
import { SegmentType } from '@goldcast/player-config/src/types/Segment/Type';
import { approxEquals } from '@goldcast/player-config/src/utils/compareUtils';
import { CLIP_UPDATE_TYPE } from '../clipUpdateDataUtilsWithSplits/constants';
import { updateClipDataWithSplits } from '../clipUpdateDataUtilsWithSplits/updateWithSplits';
import { remotionPlayerStateStore } from '../store/remotionPlayerState';
import { getSegmentAtCurrentFrame, getSegmentTimeFromPlayerTime, getPlayerTimeFromSegmentTime } from '../timeUtils';
import { snapCuePointToWordBoundary } from '../snapToWordBoundaryUtil';
import { snapCuePointToSentenceBoundary } from '../snapToSentenceBoundaryUtil';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import featureFlagStore from '@/stores/featureFlagStore';
import { showErrorToast } from '@/libs/toast/toast';
import { useRemotionPlayerSeek } from '@/App/remotion/MainPlayer/ClipPlayerV2WithNewTimeline/hooks/useRemotionPlayerSeek';
import { isSplitsFeatureEnabled } from '@/stores/clip';
import { FeatureFlagKeys } from '@/services/featureFlag';

const MIN_SPLIT_DISTANCE_SECONDS = 3;

export type SplitMode = 'precise' | 'word' | 'sentence';

const REASON_FOR_DISABLED_SPLITS = {
  NOT_ENABLED: 'Splits are not enabled for this clip',
  TRANSITION_RECORDING: 'Splits are not supported for transition recordings',
  NOT_COMPOSITE_SEGMENT: 'Splits are not supported for this segment',
  NOT_ENOUGH_DISTANCE: 'Not enough distance to the nearest end of the composite segment'
};

export default function useSplits() {
  const { clipData } = useClipsContext();
  const { seekTo } = useRemotionPlayerSeek();

  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);

  const { currentTime } = useSyncExternalStore(
    remotionPlayerStateStore.subscribe,
    remotionPlayerStateStore.getSnapshot
  );

  const fps = getClipFPS(clipData);

  const currentSegment = getSegmentAtCurrentFrame(currentTime, clipData);
  const isCurrentSegmentComposite = currentSegment?.type === SegmentType.COMPOSITE;

  const distanceToNearestEndOfCompositeSegment = isCurrentSegmentComposite
    ? Math.min(
        Math.abs(currentSegment.toFrame - currentTime * fps),
        Math.abs(currentSegment.fromFrame - currentTime * fps)
      )
    : Infinity;

  const isTranslated = useMemo(() => {
    const originalLang = clipData.asset_metadata.original_lang;
    const selectedLang = clipData.asset_metadata.selected_lang;
    return originalLang && selectedLang && originalLang !== selectedLang;
  }, [clipData]);

  const isSplitsFeatureFlagEnabled = useMemo(() => {
    return isSplitsFeatureEnabled(featureFlags, clipData);
  }, [clipData, featureFlags]);

  const canUseSplits = useMemo(() => {
    return (
      isCurrentSegmentComposite &&
      distanceToNearestEndOfCompositeSegment > MIN_SPLIT_DISTANCE_SECONDS * fps &&
      isSplitsFeatureFlagEnabled &&
      !isTranslated
    );
  }, [
    isCurrentSegmentComposite,
    distanceToNearestEndOfCompositeSegment,
    fps,
    isSplitsFeatureFlagEnabled,
    isTranslated
  ]);

  const reasonForDisabledSplits = useMemo(() => {
    if (isTranslated) {
      return REASON_FOR_DISABLED_SPLITS.TRANSITION_RECORDING;
    }
    if (!isSplitsFeatureFlagEnabled) {
      return REASON_FOR_DISABLED_SPLITS.NOT_ENABLED;
    }
    if (!isCurrentSegmentComposite) {
      return REASON_FOR_DISABLED_SPLITS.NOT_COMPOSITE_SEGMENT;
    }
    if (distanceToNearestEndOfCompositeSegment <= MIN_SPLIT_DISTANCE_SECONDS * fps) {
      return REASON_FOR_DISABLED_SPLITS.NOT_ENOUGH_DISTANCE;
    }
    return '';
  }, [
    distanceToNearestEndOfCompositeSegment,
    fps,
    isCurrentSegmentComposite,
    isSplitsFeatureFlagEnabled,
    isTranslated
  ]);

  const hasExistingSplitAtPlayerTime = useCallback(
    (playerTime: number) => {
      const compositeSegments = getAllCompositeSegments(clipData);
      const toleranceSeconds = 0.5 / fps;
      return compositeSegments.some(segment => approxEquals(playerTime, segment.fromFrame / fps, toleranceSeconds));
    },
    [clipData, fps]
  );

  const performSplit = useCallback(
    (mode: SplitMode) => {
      const currentTime = remotionPlayerStateStore.getSnapshot().currentTime;
      const segment = getSegmentAtCurrentFrame(currentTime, clipData);
      let startTime = currentTime;
      if (segment?.type === SegmentType.COMPOSITE) {
        const segmentTime = getSegmentTimeFromPlayerTime(currentTime, clipData, segment.id);
        const captions =
          getCaptionsForSegment({
            clip: clipData,
            segmentId: segment.id,
            isSplitsTranslationEnabled: !!featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_CL_Splits_Translations]
          }) ?? [];
        const snappedSegmentTime =
          mode === 'precise'
            ? segmentTime
            : mode === 'word'
              ? snapCuePointToWordBoundary(segmentTime, captions)
              : snapCuePointToSentenceBoundary(segmentTime, captions);
        startTime = getPlayerTimeFromSegmentTime(snappedSegmentTime, clipData, segment.id);
      }

      if (mode !== 'precise' && hasExistingSplitAtPlayerTime(startTime)) {
        showErrorToast('Split already exists at this location');
        return;
      }

      updateClipDataWithSplits({
        clipId: clipData.id,
        actionType: CLIP_UPDATE_TYPE.SPLIT_SEGMENT,
        startTime
      });
      seekTo(startTime);
    },
    [clipData, hasExistingSplitAtPlayerTime, seekTo]
  );

  return {
    canUseSplits,
    reasonForDisabledSplits,
    performSplit
  };
}
